import { useEffect } from 'react';

import { useFeatureFlags } from 'Utilities/hooks';

const describeReason = (reason: unknown) => {
  if (reason instanceof Error) {
    return {
      error: reason,
      extra: { type: 'Error' },
    };
  }

  if (reason instanceof XMLHttpRequest) {
    return {
      error: new Error(`Unhandled XHR rejection: ${reason.status} ${reason.statusText || '(no status text)'} - ${reason.responseURL || '(no URL)'}`),
      extra: {
        readyState: reason.readyState,
        responseURL: reason.responseURL,
        status: reason.status,
        statusText: reason.statusText,
        type: 'XMLHttpRequest',
      },
    };
  }

  if (reason && typeof reason === 'object') {
    return {
      error: new Error(`Unhandled rejection: ${Object.prototype.toString.call(reason)}`),
      extra: {
        keys: Object.keys(reason),
        reason,
        type: typeof reason,
      },
    };
  }

  if (typeof reason === 'string') {
    return {
      error: new Error(reason),
      extra: { reason, type: 'string' },
    };
  }

  return {
    error: new Error(`Unhandled rejection: ${String(reason)}`),
    extra: { reason, type: typeof reason },
  };
};

export const SentryUnhandledRejection = () => {
  const { sentryUnhandledRejection } = useFeatureFlags();

  useEffect(() => {
    if (!sentryUnhandledRejection) {
      return () => {};
    }

    const handler = (event: PromiseRejectionEvent) => {
      const { error, extra } = describeReason(event.reason);
      window?.Sentry?.captureException(error, {
        extra,
        tags: { source: 'unhandledRejectionDebug' },
      });
    };

    window.addEventListener('unhandledrejection', handler);

    return () => {
      window.removeEventListener('unhandledrejection', handler);
    };
  }, [sentryUnhandledRejection]);

  return null;
};
